import React, { useCallback, useEffect, useState } from 'react';

/**
 * PaymentTermsField — a contact's payment terms (Card P161 item 3).
 *
 * DANIEL'S RULING (29 Oct 2026): invoices are DUE ON DEMAND by default, with
 * terms adjustable per individual client.
 *
 * ⛔⛔ TWO GATES, AND THE READ-ONLY STATE IS A REAL STATE.
 * `finance:read` sees the terms; `finance:terms:write` changes them. A viewer
 * with only the first sees the value and NO control — not a disabled input that
 * looks broken, and not a hidden field that makes the unpaid count on the CEO
 * dashboard unexplainable to the person reading it. Without `finance:read` the
 * component renders nothing at all.
 *
 * ⛔ "DUE ON DEMAND" AND "SET TO 0" READ DIFFERENTLY, deliberately. The server
 * reports `isDefault`, and a screen that collapsed the two would hide from the
 * next editor whether a colleague had already decided.
 *
 * ⚠ THIS IS NOT GHL'S DUE DATE. The line under the field says so, because the
 * two numbers sit three panels apart on the dashboard and adding them
 * double-counts the same invoice.
 */

const PRESETS = [0, 7, 14, 30];

export default function PaymentTermsField({ contactId, user, onSaved = null }) {
  const perms = (user && user.permissions) || [];
  const canRead = perms.includes('finance:read');
  const canWrite = perms.includes('finance:terms:write');

  const [state, setState] = useState('loading');
  const [terms, setTerms] = useState(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
    if (!api || typeof api.getPaymentTerms !== 'function') { setState('failed'); return; }
    try {
      const r = await api.getPaymentTerms({ contactId });
      if (r && r.error) throw new Error(r.message || 'failed');
      setTerms(r);
      setDraft(String(r.termsDays));
      setNote(r.note || '');
      setState('ok');
    } catch (e) {
      /*
       * ⛔ FAILED IS NOT ZERO. "We could not read the terms" must never render
       * as "due on demand" — that is a policy statement, and it would be made
       * by an outage.
       */
      setState('failed');
    }
  }, [contactId]);

  useEffect(() => { if (canRead && contactId) load(); }, [canRead, contactId, load]);

  const save = useCallback(async () => {
    const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
    if (!api || typeof api.setPaymentTerms !== 'function') return;
    setSaving(true); setError(null);
    try {
      const r = await api.setPaymentTerms({ contactId, termsDays: draft, note });
      if (r && r.error) { setError(r.message || 'Could not save.'); return; }
      await load();
      if (onSaved) onSaved(r);
    } catch (e) {
      setError('Could not save.');
    } finally {
      setSaving(false);
    }
  }, [contactId, draft, note, load, onSaved]);

  // ⛔ No finance:read ⇒ nothing. Not an empty box, not a placeholder.
  if (!canRead) return null;

  if (state === 'loading') {
    return <div data-testid="payment-terms" style={S.wrap}><div style={S.label}>Payment terms</div>
      <div style={S.muted}>Loading…</div></div>;
  }
  if (state === 'failed') {
    return <div data-testid="payment-terms" style={S.wrap}><div style={S.label}>Payment terms</div>
      <div style={S.failed} data-testid="payment-terms-failed">
        Could not be loaded — unknown right now, not &ldquo;due on demand&rdquo;.
      </div></div>;
  }

  const days = terms ? terms.termsDays : 0;
  const summary = days === 0 ? 'Due on demand' : `${days} day${days === 1 ? '' : 's'}`;

  return (
    <div data-testid="payment-terms" style={S.wrap}>
      <div style={S.label}>Payment terms</div>

      <div style={S.value} data-testid="payment-terms-value">
        {summary}
        {terms && terms.isDefault && (
          <span style={S.muted} data-testid="payment-terms-default"> · default (not set for this client)</span>
        )}
        {terms && !terms.isDefault && terms.setBy && (
          <span style={S.muted} data-testid="payment-terms-setby">
            {' '}· set by {terms.setBy}{terms.setAt ? ` on ${new Date(terms.setAt).toLocaleDateString()}` : ''}
          </span>
        )}
      </div>

      {terms && terms.note && (
        <div style={S.note} data-testid="payment-terms-note">{terms.note}</div>
      )}

      {/*
        * ⚠ ONE LINE EXPLAINING THE DIFFERENCE, on the screen where terms are
        * set. "Overdue" comes from GHL's own due date and is a different set of
        * invoices; the two overlap, so adding them double-counts.
        */}
      <div style={S.help} data-testid="payment-terms-help">
        Terms decide when an unpaid invoice starts needing attention. They are separate from
        &ldquo;Overdue&rdquo;, which uses GHL&rsquo;s own due date — the two overlap, so never add them together.
      </div>

      {canWrite && (
        <div style={S.editor} data-testid="payment-terms-editor">
          <select
            data-testid="payment-terms-select"
            value={PRESETS.includes(Number(draft)) ? String(draft) : 'custom'}
            onChange={(e) => setDraft(e.target.value === 'custom' ? String(draft) : e.target.value)}
            style={S.input}
          >
            {PRESETS.map((d) => (
              <option key={d} value={String(d)}>{d === 0 ? 'Due on demand (0 days)' : `${d} days`}</option>
            ))}
            <option value="custom">Custom…</option>
          </select>
          <input
            data-testid="payment-terms-days"
            type="number" min="0" max="365" value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Payment terms in days"
            style={{ ...S.input, width: 90 }}
          />
          <input
            data-testid="payment-terms-note-input"
            type="text" value={note} placeholder="Note (optional)"
            onChange={(e) => setNote(e.target.value)}
            aria-label="Payment terms note"
            style={{ ...S.input, flex: 1, minWidth: 120 }}
          />
          <button type="button" onClick={save} disabled={saving}
                  data-testid="payment-terms-save" style={S.save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      {error && <div style={S.failed} data-testid="payment-terms-error">{error}</div>}
    </div>
  );
}

const S = {
  wrap: { padding: '10px 0', borderTop: '1px solid #E9ECEF' },
  label: { fontSize: 11, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.3 },
  value: { fontSize: 13, color: '#212529', marginTop: 3 },
  muted: { fontSize: 12, color: '#868E96', fontWeight: 400 },
  note: { fontSize: 12, color: '#495057', marginTop: 3, fontStyle: 'italic' },
  help: { fontSize: 11, color: '#868E96', marginTop: 5, lineHeight: 1.45 },
  editor: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' },
  input: { fontSize: 12, padding: '4px 6px', border: '1px solid #CED4DA', borderRadius: 4 },
  save: { fontSize: 12, padding: '5px 12px', border: 'none', borderRadius: 4, background: '#1B6E3C', color: '#fff', cursor: 'pointer' },
  failed: { fontSize: 12, color: '#9B2C2C', marginTop: 5 },
};
